import { useState, useEffect, useMemo } from 'react'
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { LogOut, ChevronDown, Search, Settings } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { api } from '../lib/api'
import { CommandPalette } from './CommandPalette'
import drinxLogo from '../assets/drinx_logo_white.png'

// Sidebar visibility per role. Mirrors the worker's `requireRole([...])`
// gates plus a sensible read-access layer for non-mutating roles. Roles that
// aren't included for an item don't see it in the sidebar — but the worker
// still rejects any mutation they attempt, so the sidebar gate is UX, not
// security. See `docs/admin/admin-users.md` for the canonical access matrix.
// SECTIONS is shared with `pages/PermissionsMatrix.tsx`.
import { SECTIONS } from '../lib/admin-nav'

const STORAGE_KEY = 'drinx-admin-sidebar-collapsed'

export default function Layout() {
  const { user, logout, can } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Open-issues count drives the badge on the Issues nav row. Polled every 30 s.
  const { data: openIssues } = useQuery<{ count: number }>({
    queryKey: ['issues-open-count'],
    queryFn: () => api.get('/issues/open-count').then(r => r.data),
    refetchInterval: 30_000,
  })
  const { data: openSupport } = useQuery<{ count: number }>({
    queryKey: ['support-open-count'],
    queryFn: () => api.get('/support/open-count').then(r => r.data),
    refetchInterval: 30_000,
  })
  const badges: Record<string, number> = {
    issues: openIssues?.count ?? 0,
    support: openSupport?.count ?? 0,
  }

  // Cmd+K / Ctrl+K opens the global command palette from anywhere.
  // The check on `target` keeps the shortcut working even when typed in a
  // form field (cmdk's input handles its own keys internally).
  const [paletteOpen, setPaletteOpen] = useState(false)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const cmdOrCtrl = e.metaKey || e.ctrlKey
      if (cmdOrCtrl && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      } else if (e.key === 'Escape') {
        setPaletteOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Visible sections (after super-admin filtering on items)
  // Filter sidebar by role. An item is visible if:
  //   - it has no role restrictions, OR
  //   - the user can() one of its roles, OR
  //   - it's `superAdminOnly` and the user is super_admin.
  // Sections with zero visible items are dropped entirely.
  const visibleSections = useMemo(() => {
    return SECTIONS
      .map(s => ({
        ...s,
        items: s.items.filter(i => {
          if (i.superAdminOnly) return can(['super_admin'])
          if (i.roles && i.roles.length > 0) return can(i.roles)
          return true
        }),
      }))
      .filter(s => s.items.length > 0)
  }, [can])

  // Collapsed-section state, persisted to localStorage so it survives reloads.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  })

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed)) } catch { /* ignore */ }
  }, [collapsed])

  // Auto-expand the section containing the active route on navigation. Never
  // auto-collapse — respect the user's explicit choices.
  useEffect(() => {
    const active = visibleSections.find(s => s.items.some(i => i.to === location.pathname))
    if (active && collapsed[active.id]) {
      setCollapsed(c => ({ ...c, [active.id]: false }))
    }
  }, [location.pathname, visibleSections, collapsed])

  function toggle(id: string) {
    setCollapsed(c => ({ ...c, [id]: !c[id] }))
  }

  return (
    <div className="flex h-screen bg-[#111111] text-white">
      <aside className="w-56 flex-shrink-0 bg-[#1a1a1a] border-r border-[#2e2e2e] flex flex-col">
        <div className="px-5 py-5 border-b border-[#2e2e2e]">
          <div className="flex items-center gap-2">
            <img src={drinxLogo} alt="DrinX" className="h-7 w-auto" />
            <span className="text-xs text-gray-500 ml-1">Admin</span>
          </div>
          {/* Quick-access search opens the global command palette. The Cmd/Ctrl+K
              shortcut is the primary entry point; this button is the discoverable
              fallback for users who don't know the shortcut. */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="mt-4 w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0f0f0f] border border-[#2e2e2e] text-sm text-gray-400 hover:text-white hover:border-[#3e3e3e] transition-colors"
            aria-label="Open search"
          >
            <Search size={14} />
            <span className="flex-1 text-left">Search…</span>
            <kbd className="text-[10px] text-gray-600 bg-[#1a1a1a] px-1.5 py-0.5 rounded">⌘K</kbd>
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {visibleSections.map(section => {
            const isCollapsed = !!collapsed[section.id]
            return (
              <div key={section.id} className="mb-1">
                <button
                  type="button"
                  onClick={() => toggle(section.id)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] text-gray-500 hover:text-gray-300 uppercase tracking-widest transition-colors"
                >
                  <span>{section.label}</span>
                  <ChevronDown
                    size={12}
                    className={`transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {section.items.map(({ to, icon: Icon, label, badgeKey }) => {
                      const badge = badgeKey ? badges[badgeKey] : 0
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? 'bg-[#C41E3A]/15 text-[#C41E3A] font-medium'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                            }`
                          }
                        >
                          <Icon size={17} />
                          <span className="flex-1">{label}</span>
                          {badge > 0 && (
                            <span className="text-[10px] font-semibold bg-[#C41E3A] text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                              {badge > 99 ? '99+' : badge}
                            </span>
                          )}
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div className="px-3 py-4 border-t border-[#2e2e2e]">
          <button
            onClick={() => navigate('/profile')}
            className="w-full px-3 py-2 mb-1 rounded-lg text-left hover:bg-white/5 transition-colors group"
          >
            <p className="text-sm font-medium text-white truncate group-hover:text-[#C41E3A] transition-colors">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <Settings size={17} />
            Profile & Password
          </button>
          <button
            onClick={logout}
            className="flex w-full items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut size={17} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  )
}
